import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * A custom element that displays recently viewed products in the cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} productsContainer - The container for product cards.
 *
 * @extends {Component<Refs>}
 */
class CartRecentlyViewedComponent extends Component {
  /** @type {string | null} */
  #cachedHTML = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const dialog = this.closest('dialog');
    if (!dialog) return;

    if (dialog.open) {
      this.#loadProducts();
    }

    this.#dialogObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'open' && dialog.open) {
          this.#loadProducts();
        }
      }
    });

    this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
  }

  async #loadProducts() {
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length === 0) {
      this.hidden = true;
      return;
    }

    const { productsContainer } = this.refs;
    if (!productsContainer) return;

    if (this.#cachedHTML) {
      productsContainer.innerHTML = this.#cachedHTML;
      this.hidden = false;
      return;
    }

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML('cart-recently-viewed', false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.getElementById('cart-recently-viewed-results');

      if (!results) return;

      const cards = Array.from(results.querySelectorAll('[data-product-id]'));

      const orderedCards = viewedProducts
        .map((/** @type {string} */ id) => cards.find((card) => card.dataset.productId === id))
        .filter(Boolean);

      if (orderedCards.length === 0) {
        this.hidden = true;
        return;
      }

      productsContainer.innerHTML = '';

      for (const card of orderedCards) {
        productsContainer.appendChild(card);
      }

      this.#cachedHTML = productsContainer.innerHTML;
      this.hidden = false;
    } catch (error) {
      console.error('Failed to load recently viewed products:', error);
      this.hidden = true;
    }
  }
}

if (!customElements.get('cart-recently-viewed')) {
  customElements.define('cart-recently-viewed', CartRecentlyViewedComponent);
}
